import { motion } from 'motion/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { User, LogOut, Settings, Shield } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useIsAdmin } from '../hooks/useIsAdmin';

interface HeaderProps {
  // darkMode: boolean;
  // setDarkMode: (dark: boolean) => void;
}

export default function Header({ /* darkMode, setDarkMode */ }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, userProfile, signOut } = useAuth();

  const { isAdmin } = useIsAdmin(user?.id);

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="fixed top-0 left-0 right-0 z-50 glass-effect border-none"
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-20 h-10 bg-gradient-to-r from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">MP3</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white hidden xs:block">
                Random Selector
              </h1>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white block xs:hidden">
                Random Selector
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 hidden lg:block">
                Sélecteur aléatoire de fichiers MP3
              </p>
            </div>
          </div>
          {/* Menu desktop */}
          <nav className="hidden lg:flex items-center space-x-8">
            <a href="/#features" className="relative text-gray-700 dark:text-gray-300 hover:text-primary-500 transition duration-300 after:content-[''] after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-0.5 after:bg-primary-500 after:transition-all after:duration-300 hover:after:w-full transform hover:scale-110">
              Fonctionnalités
            </a>
            <a href="/#screenshots" className="relative text-gray-700 dark:text-gray-300 hover:text-primary-500 transition duration-300 after:content-[''] after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-0.5 after:bg-primary-500 after:transition-all after:duration-300 hover:after:w-full transform hover:scale-110">
              Captures d'écran
            </a>
            <a href="/faq" className="relative text-gray-700 dark:text-gray-300 hover:text-primary-500 transition duration-300 after:content-[''] after:absolute after:left-0 after:-bottom-1 after:w-0 after:h-0.5 after:bg-primary-500 after:transition-all after:duration-300 hover:after:w-full transform hover:scale-110">
              FAQ
            </a>
          </nav>
          {/* Bouton menu mobile */}
          <button
            className="lg:hidden p-2 rounded-lg glass-effect ml-2"
            aria-label="Ouvrir le menu"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-gray-700 dark:text-gray-300">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          {/* Actions à droite */}
          <div className="flex items-center space-x-4">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-xl px-4 py-2 border border-white/20 hover:bg-white/20 dark:hover:bg-gray-900/20 transition-all duration-200"
                >
                  <User className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                  <span className="text-gray-700 dark:text-gray-300 hidden md:block">
                    {userProfile?.full_name || 'Mon compte'}
                  </span>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                    <div className="py-2">
                      <Link
                        to="/dashboard"
                        className="block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Settings className="h-4 w-4 inline mr-2" />
                        Dashboard
                      </Link>
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="h-4 w-4 inline mr-2" />
                        Mon profil
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-purple-600 dark:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <Shield className="h-4 w-4 inline mr-2" />
                          Administration
                        </Link>
                      )}
                      <hr className="my-2 border-gray-200 dark:border-gray-700" />
                      <button
                        onClick={handleSignOut}
                        className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        <LogOut className="h-4 w-4 inline mr-2" />
                        Se déconnecter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Texte visible sur les écrans larges (>520px) */}
                <Link
                  to="/login"
                  className="btn-primary max-[520px]:hidden"
                >
                  Se connecter
                </Link>

                {/* Icône visible sur les écrans petits (<=520px) */}
                <Link
                  to="/login"
                  aria-label="Se connecter"
                  className="hidden max-[520px]:inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg px-3 py-2 border border-white/20 hover:bg-white/20 dark:hover:bg-gray-900/20 transition-all duration-200"
                >
                  <User className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                </Link>
              </>
            )}

          </div>
        </div>
      </div>
      {/* Menu mobile overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex flex-col items-center pt-20 lg:hidden" onClick={() => setMenuOpen(false)}>
          <nav className="bg-white dark:bg-gray-900 rounded-xl shadow-lg p-8 flex flex-col space-y-6 text-lg min-w-[70vw] max-w-xs" onClick={e => e.stopPropagation()}>
            <a href="/#features" className="text-gray-800 dark:text-gray-100 hover:text-primary-500" onClick={() => setMenuOpen(false)}>
              Fonctionnalités
            </a>
            <a href="/#screenshots" className="text-gray-800 dark:text-gray-100 hover:text-primary-500" onClick={() => setMenuOpen(false)}>
              Captures d'écran
            </a>
            <a href="/faq" className="text-gray-800 dark:text-gray-100 hover:text-primary-500" onClick={() => setMenuOpen(false)}>
              FAQ
            </a>
            <a href="/#download" className="text-gray-800 dark:text-gray-100 hover:text-primary-500" onClick={() => setMenuOpen(false)}>
              Télécharger
            </a>
            <button
              onClick={() => setMenuOpen(false)}
              className="mt-4 text-gray-500 hover:text-primary-500"
            >
              Fermer
            </button>
          </nav>
        </div>
      )}
    </motion.header>
  );
}
